import React, { useState } from 'react';
import DataEntryModal from '@grnhse/seedling/lib/birch/components/modals/data_entry_modal';
import { SelectionsHelper } from 'talent_matching/hooks/use_selections_helper';
import { withLocalePrefix } from 'shared/utils/translation';
import { useTalentMatchingTabContext } from 'talent_matching/contexts/talent_matching_tab_context';
import { useTrackBulkAction } from 'talent_matching/lib/use_track_bulk_action';
import EmailSection from './email_section';
import Form from '@grnhse/seedling/lib/birch/components/forms/form';
import ModalFooter from './modal_footer';
import SkeletonLoader from '@grnhse/seedling/lib/birch/components/skeleton_loader';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { Body } from '@grnhse/seedling/lib/birch/typography';
import { SecondaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import { useSendEmailData } from 'talent_matching/hooks/use_send_email_data';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import { standardRequestWithErrorFlash } from 'shared/utils/request';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';

const t = withLocalePrefix('talent_matching.bulk_actions.send_email');

type Props = {
  isOpen: boolean;
  closeModal: () => void;
  selectionsHelper: SelectionsHelper;
  loadingComplete: () => void;
};

type SendEmailForm = {
  template: string;
  from: Item;
  cc: Item[];
  cc_recruiter: boolean;
  cc_coordinator: boolean;
  include_self: boolean;
  subject: string;
  html_body: string;
  application_ids: number[];
};

const SendEmailModal = ({
  isOpen,
  closeModal,
  selectionsHelper,
  loadingComplete,
}: Props) => {
  const {
    paths: { emailModalPath },
    appReviewSessionId,
  } = useTalentMatchingTabContext();
  const trackBulk = useTrackBulkAction('tm_app_review', appReviewSessionId);
  const [submitting, setSubmitting] = useState(false);

  const { data, error, refetch, isFetching, isLoading } =
    useSendEmailData(emailModalPath);

  const loading = isFetching || isLoading;

  if (!loading) {
    loadingComplete();
  }

  if (loading || error || !data) {
    return (
      <DataEntryModal
        isOpen={isOpen}
        onDismiss={closeModal}
        ariaLabel={t('title')}
        size="large"
      >
        <DataEntryModal.Header onClick={closeModal}>
          {t('title', { count: selectionsHelper.totalSelected })}
        </DataEntryModal.Header>

        <DataEntryModal.Body>
          {loading && <SkeletonLoader columnsPerRow={1} rows={5} />}

          {!loading && (error || !data) && (
            <Stack
              spacing={Tokens.spacing['2']}
              direction="row"
              alignItems="center"
            >
              <Body>{t('error_loading')}</Body>
              <SecondaryButton onClick={() => refetch()} size="md">
                {t('retry')}
              </SecondaryButton>
            </Stack>
          )}
        </DataEntryModal.Body>
      </DataEntryModal>
    );
  }

  const onSubmit = async (formData: SendEmailForm) => {
    setSubmitting(true);
    const payload = {
      email_bulk_candidate: {
        from: formData.from.value,
        subject: formData.subject,
        html_body: formData.html_body,
        cc: formData.cc.map((c) => c.value).join(','),
        cc_recruiter: formData.cc_recruiter && '1',
        cc_coordinator: formData.cc_coordinator && '1',
        include_self: formData.include_self && '1',
      },
      application_ids: selectionsHelper.applicationIds,
    };

    try {
      const res = await standardRequestWithErrorFlash.post(
        data.bulkEmailCandidatesPath,
        payload,
        {
          successMessage: t('success', {
            count: selectionsHelper.totalSelected,
          }),
        }
      );
      if (res.data.status === 'success') {
        trackBulk('send_email', selectionsHelper.applicationIds);
        closeModal();
      }
    } catch {
      setSubmitting(false);
    }
  };

  const defaultValues = {
    template: data.defaultTemplate,
    from: '',
    cc: [],
    cc_recruiter: false,
    cc_coordinator: false,
    include_self: false,
    subject: '',
    html_body: '',
    application_ids: selectionsHelper.applicationIds,
  };

  return (
    <DataEntryModal
      isOpen={isOpen}
      onDismiss={closeModal}
      ariaLabel={t('title')}
      size="large"
    >
      <Form onSubmit={onSubmit} defaultValues={defaultValues} mode="onSubmit">
        <DataEntryModal.Header onClick={closeModal}>
          {t('title', { count: selectionsHelper.totalSelected })}
        </DataEntryModal.Header>
        <DataEntryModal.Body>
          <EmailSection
            totalSelected={selectionsHelper.totalSelected}
            templateOptions={data.templateOptions}
            fromOptions={data.fromOptions}
            getCcOptionsPath={data.getCcOptionsPath}
            displayDomainWarning={data.displayDomainWarning}
            tokens={data.tokens}
          />
        </DataEntryModal.Body>
        <ModalFooter closeModal={closeModal} disableButtons={submitting} />
      </Form>
    </DataEntryModal>
  );
};

export default SendEmailModal;
